import { useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import HomePage from "./Pages/HomePage";
import RegPage from "./Pages/RegPage";
import BookingPage from "./Pages/BookingPage";
import InfoPage from "./Pages/InfoPage";
import LoginPage from "./Pages/LoginPage";
import GoogleCallbackPage from "./Pages/GoogleCallbackPage";
import ProfilePage from "./Pages/ProfilePage";
import BookingDay from "./Pages/BookingDay";
import BookingAction from "./Pages/BookingAction";
import PoliciesPage from "./Pages/PoliciesPage";
import AdminPage from "./Pages/admin/AdminPage";
import ForgotPasswordPage from "./Pages/ForgotPasswordPage";
import ResetPasswordPage from "./Pages/ResetPasswordPage";
import { LanguageProvider } from "./context/LanguageContext";
import RequireAuth from "./components/auth/RequireAuth";
import RequireOwner from "./components/auth/RequireOwner";
import { trackPageVisit } from "./api/attendance";
import { getOrCreateSessionId } from "./utils/sessionId";
import BookingAvailabilityGate from "./components/BookingAvailabilityGate";
import CookieBanner from "./components/CookieBanner";
import { ConsentProvider, useConsent } from "./consent/ConsentContext";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const getNavigationType = () => {
      if (typeof window === "undefined" || typeof window.performance === "undefined") {
        return undefined;
      }
      if (typeof window.performance.getEntriesByType === "function") {
        const [entry] = window.performance.getEntriesByType("navigation");
        return entry?.type;
      }
      const nav = window.performance.navigation;
      if (nav && typeof nav.TYPE_RELOAD !== "undefined" && nav.type === nav.TYPE_RELOAD) {
        return "reload";
      }
      return undefined;
    };

    const navigationType = getNavigationType();
    const isReloadNavigation = navigationType === "reload";

    if (hash) {
      if (isReloadNavigation) {
        window.scrollTo({ top: 0, behavior: "auto" });
        return undefined;
      }

      const scrollToHash = () => {
        try {
          const element = document.querySelector(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            return true;
          }
        } catch {
          // Ignore invalid selector hashes (e.g., OAuth id_token fragments)
        }
        return false;
      };

      if (scrollToHash()) return;
      const timeout = setTimeout(() => {
        if (!scrollToHash()) {
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      }, 0);
      return () => clearTimeout(timeout);
    }

    window.scrollTo({ top: 0, behavior: "auto" });
    return undefined;
  }, [pathname, hash]);

  return null;
}

function AttendanceTracker() {
  const { pathname } = useLocation();
  const lastPathRef = useRef(null);
  const { canUseAnalytics } = useConsent();

  useEffect(() => {
    if (!canUseAnalytics || !pathname || pathname.startsWith("/admin")) return;
    const sessionId = getOrCreateSessionId();
    const referrer =
      lastPathRef.current || (typeof document !== "undefined" ? document.referrer : null);
    lastPathRef.current = pathname;
    trackPageVisit({ sessionId, path: pathname, referrer });
  }, [canUseAnalytics, pathname]);

  return null;
}

function App() {
  return (
    <LanguageProvider>
      <ConsentProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AttendanceTracker />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/register" element={<RegPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/info" element={<InfoPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/booking-day"
              element={
                <BookingAvailabilityGate>
                  <BookingDay />
                </BookingAvailabilityGate>
              }
            />
            <Route
              path="/booking-night"
              element={<Navigate to="/booking-day" replace />}
            />
            <Route path="/b/:token" element={<BookingAction />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route
              path="/admin"
              element={
                <RequireOwner>
                  <AdminPage />
                </RequireOwner>
              }
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
            <Route path="/profile" element={<RequireAuth> <ProfilePage /> </RequireAuth>} />
            <Route path="*" element={<div>404 Not Found</div>} />
          </Routes>
          <CookieBanner />
        </BrowserRouter>
      </ConsentProvider>
    </LanguageProvider>
  );
}

export default App;
export { AttendanceTracker };
